import { useState, useCallback, FC, useMemo, CSSProperties } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { actions, selectors } from '@altermaind/core/i18n';
import { Box, Button, Typography } from '@altermaind/web/ui';
import { Routes } from '@/_shared/constants/routes';
import { getEnvironment } from '@/_shared/helpers';
import { useDialog } from '@/_shared/hooks/useDialog';
import pjson from '../../../../package.json';
import { Form } from '../Form';

/**
 * Component that toggles the developer panel visibility based on the application environment.
 * It displays the environment info and allows toggling the raw translation keys display.
 *
 * @return {ReactElement|null} The developer panel if not in production, otherwise null.
 *
 * Example usage:
 * ```jsx
 * <ToggleDevPanel />
 * ```
 */
export const ToggleDevPanel: FC = () => {
  const { isProduction } = getEnvironment();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const showRawKeys = useSelector(selectors.selectShowRawKeys);

  const { openDialog } = useDialog('quizStarter');
  const { openDialog: openDialogVideoStarter } = useDialog('videoStarter');
  const { openDialog: openDialogDocumentStarter } = useDialog('documentStarter');
  const quizId = '69304ee6f030f9497a8ae4d8'; // Example quiz ID
  const videoTemplateId = '69304ee6f030f9497a8ae4d8'; // Example video ID
  const documentTemplateId = '69304ee6f030f9497a8ae4d8'; // Example document ID

  const { control, watch } = useForm({
    defaultValues: {
      quizTemplateId: quizId,
      videoTemplateId,
      documentTemplateId,
    },
  });

  const fieldValues = watch();

  const toggleShowRawKeys = useCallback(() => {
    dispatch(actions.setShowRawKeys(!showRawKeys));
  }, [dispatch, showRawKeys]);

  const goToConversations = useCallback(() => {
    navigate(Routes.Conversations);
  }, [navigate]);

  const goToComponents = useCallback(() => {
    navigate('/components');
  }, [navigate]);

  const { current } = getEnvironment();

  const [hover, setHover] = useState(false);
  const [showConversationsSubmenu, setShowConversationsSubmenu] = useState(false);
  const borderSize = 5;

  const memoizedStyle: CSSProperties = useMemo(
    () => ({
      transform: hover ? `translateX(-${borderSize}px)` : 'translateX(-95%)',
      transition: ' transform 250ms ease-in-out',
      userSelect: 'none',
    }),
    [hover],
  );

  if (isProduction) {
    return null;
  }

  return (
    <Box
      position='fixed'
      backgroundColor='primary'
      top={10}
      left={0}
      shadow='medium'
      zIndex={9999}
      style={memoizedStyle}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <Box pl='s' pr='s' pt='xs' pb='xs' mb='s'>
        <Typography variant='p2/label'>
          Env:{' '}
          <Typography variant='p2/light' as='span'>
            {current.toUpperCase()}
          </Typography>
        </Typography>
        <Typography variant='p2/label'>
          Version:{' '}
          <Typography variant='p2/light' as='span'>
            {pjson.version}
          </Typography>
        </Typography>
        <Typography variant='p2/label' mt='s'>
          Functionality
        </Typography>
        <Typography cursor='pointer' variant='p2' onClick={toggleShowRawKeys}>
          - Translations keys
        </Typography>
        <Typography cursor='pointer' variant='p2' onClick={goToComponents}>
          - Components
        </Typography>
        <Typography cursor='pointer' variant='p2' onClick={goToConversations}>
          - Conversations
        </Typography>
        <Typography
          cursor='pointer'
          variant='p2'
          onClick={() => setShowConversationsSubmenu(!showConversationsSubmenu)}
        >
          - Training
        </Typography>
        {showConversationsSubmenu && (
          <Box mt='xxs' borderLeft='1px solid' borderColor='white' pl='xs'>
            <Box>
              <Typography variant='p2'>Quiz</Typography>
              <Box display='flex' flexDirection='row' maxWidth='100%'>
                <Form.FormInput
                  // @ts-ignore
                  control={control}
                  name='quizTemplateId'
                  label=''
                  placeholder={quizId}
                  mr='xxs'
                />
                <Button
                  onClick={() => openDialog({ quizId: fieldValues.quizTemplateId.trim() })}
                  variant='positive'
                >
                  Apri
                </Button>
              </Box>
            </Box>
            <Box mt='xs'>
              <Typography variant='p2'>Video</Typography>
              <Box display='flex' flexDirection='row' maxWidth='100%'>
                <Form.FormInput
                  // @ts-ignore
                  control={control}
                  name='videoTemplateId'
                  label=''
                  placeholder={quizId}
                  mr='xxs'
                />
                <Button
                  onClick={() =>
                    openDialogVideoStarter({ videoId: fieldValues.videoTemplateId.trim() })
                  }
                  variant='positive'
                >
                  Apri
                </Button>
              </Box>
            </Box>
            <Box mt='xs'>
              <Typography variant='p2'>Document</Typography>
              <Box display='flex' flexDirection='row' maxWidth='100%'>
                <Form.FormInput
                  // @ts-ignore
                  control={control}
                  name='videoTemplateId'
                  label=''
                  placeholder={quizId}
                  mr='xxs'
                />
                <Button
                  onClick={() =>
                    openDialogDocumentStarter({ documentId: fieldValues.documentTemplateId.trim() })
                  }
                  variant='positive'
                >
                  Apri
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};
